/**
 * File: components/providers/react-query-provider.tsx
 *
 * Provider component for React Query (TanStack Query).
 */

'use client';

import { useState } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

/**
 * Props for the ReactQueryProvider component
 *
 * @interface ReactQueryProviderProps
 * @property {React.ReactNode} children - The child components to wrap with the provider
 */
interface ReactQueryProviderProps {
  children: React.ReactNode;
}

/**
 * ReactQueryProvider component that initializes and provides the QueryClient
 * This component sets up data fetching and caching for the application
 *
 * @component
 * @param {ReactQueryProviderProps} props - The component props
 * @returns {JSX.Element} The provider component with React Query configured
 */
export function ReactQueryProvider({ children }: ReactQueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Configuration optimisée pour différents types de données
            staleTime: 1000 * 60 * 5, // 5 minutes par défaut
            gcTime: 1000 * 60 * 15, // 15 minutes par défaut (augmenté)
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              // Retry logic améliorée
              if (failureCount < 2) {
                // Retry pour les erreurs réseau/serveur temporaires
                if (
                  error instanceof Error &&
                  (error.message.includes('fetch') ||
                    error.message.includes('NetworkError') ||
                    error.message.includes('timeout'))
                ) {
                  return true;
                }
              }
              return false;
            },
            // Retry avec backoff exponentiel
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
            // Réduire les refetchs inutiles par défaut
            refetchOnReconnect: false,
            refetchInterval: false,
            refetchIntervalInBackground: false,
          },
          mutations: {
            // Configuration pour les mutations
            retry: 1,
            retryDelay: 1000,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Only show DevTools in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  );
}

